import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { HttpResponse } from '@angular/common/http';
import { each } from 'lodash-es';
import { isPlatformBrowser } from "@angular/common";

const cachePersistenceKey = 'httpCache';

export interface HttpCacheEntry {
  lastUpdated: Date;
  data: HttpResponse<any>;
  expiration: number;
}

/**
 * Provides a cache facility for HTTP requests with configurable persistence policy.
 */
@Injectable()
export class HttpCacheService {
  private cachedData: { [key: string]: HttpCacheEntry } = {};
  private storage: Storage | null = null;

  constructor(@Inject(PLATFORM_ID) private platformId: Object,) {
    this.loadCacheData();
    this.setPersistence('session');
  }

  /**
   * Sets the cache data for the specified request.
   * @param url The request URL. / @param cacheKey The cachekey of the request (f.e. Sport_1)
   * @param data The received data.
   * @param lastUpdated The cache last update, current date is used if not specified.
   */
  setCacheData(
    url: string,
    data: HttpResponse<any>,
    maxAge?: number,
    lastUpdated?: Date
  ) {
    if (isPlatformBrowser(this.platformId)) {

      this.cachedData[url] = {
        lastUpdated: lastUpdated || new Date(),
        data: data,
        expiration: Date.now() + maxAge
      };

      this.saveCacheData();
    }
  }

  /**
   * Gets the cached data for the specified request.
   * @param url The request URL. / @param cacheKey The cachekey of the request (f.e. Sport_1)
   * @return The cached data or null if no cached data exists for this request.
   */
  getCacheData(url: string): HttpResponse<any> | null {
    if (isPlatformBrowser(this.platformId)) {

      const cacheEntry = this.cachedData[url];

      if (cacheEntry) {
        if (cacheEntry.expiration < Date.now()) {
          return null;
        }

        return cacheEntry.data;
      }

      return null;
    }
  }

  /**
   * Gets the cached entry for the specified request.
   * @param url The request URL. / @param cacheKey The cachekey of the request (f.e. Sport_1)
   * @return The cache entry or null if no cache entry exists for this request.
   */
  getHttpCacheEntry(url: string): HttpCacheEntry | null {
    return this.cachedData[url] || null;
  }

  /**
   * Clears the cached entry (if exists) for the specified request.
   * @param url The request URL. / @param cacheKey The cachekey of the request (f.e. Sport_1)
   */
  clearCache(url: string): void {
    if (isPlatformBrowser(this.platformId)) {

      delete this.cachedData[url];

      this.saveCacheData();
    }
  }

  /**
   * Cleans cache entries older than the specified date.
   * @param expirationDate The cache expiration date. If no date is specified, all cache is cleared.
   */
  cleanCache(expirationDate?: Date) {
    if (isPlatformBrowser(this.platformId)) {

      if (expirationDate) {
        each(this.cachedData, (value: HttpCacheEntry, key: string) => {
          if (expirationDate >= value.lastUpdated) {
            delete this.cachedData[key];
          }
        });
      } else {
        this.cachedData = {};
      }
      this.saveCacheData();
    }
  }

  /**
   * Sets the cache persistence policy.
   * Note that changing the cache persistence will also clear the cache from its previous storage.
   * @param persistence How the cache should be persisted, it can be either local or session storage, or if no value is
   *   provided it will be only in-memory (default).
   */
  setPersistence(persistence?: 'local' | 'session') {
    if (isPlatformBrowser(this.platformId)) {

      this.cleanCache();
      this.storage =
        persistence === 'local' || persistence === 'session'
          ? window[persistence + 'Storage']
          : null;
      this.loadCacheData();
    }
  }

  private saveCacheData() {
    if (isPlatformBrowser(this.platformId)) {

      try {
        if (this.storage) {
          this.storage[cachePersistenceKey] = JSON.stringify(this.cachedData);
        }
      } catch (e) {
        console.error(e); // eslint-disable-line no-console 
      }
    }
  }

  private loadCacheData() {
    if (isPlatformBrowser(this.platformId)) {

      const data = this.storage ? this.storage[cachePersistenceKey] : null;
      this.cachedData = data ? JSON.parse(data) : {};
    }
  }
}
